import { translateToString } from '@nzyme/i18n/translateToString.js';
import { capitalizeFirstLetter } from '@nzyme/utils/string/caseUtils.js';
import { addDays, differenceInMinutes, endOfWeek, isSameDay, isWithinInterval, startOfWeek, subDays } from 'date-fns';
import * as l from './formatRelativeDate.loc.js';
/**
 * Formats a date as a human-readable relative time string.
 *
 * Returns localized strings for:
 * - "Just now" for times less than 1 minute ago/from now
 * - "X minutes ago" or "in X minutes" for times less than 1 hour ago/from now
 * - Time only for today (e.g., "14:30") when includeDate is false
 * - "Today" or "Today at 14:30" for today
 * - "Yesterday" or "Yesterday at 14:30" for yesterday
 * - "Tomorrow" or "Tomorrow at 14:30" for tomorrow
 * - Day name for this week (e.g., "Monday" or "Monday at 14:30")
 * - Month and day for this year (e.g., "29 August" or "29 August at 14:30")
 * - Full date for other years (e.g., "29 August 2023" or "29 August 2023 at 14:30")
 *
 * @param date - The date to format
 * @param lang - The language to use for formatting
 * @param now - The reference date to compare against (defaults to current time)
 * @param options - Additional formatting options
 * @returns A localized relative date string
 *
 * @util
 * @__NO_SIDE_EFFECTS__
 */
export function formatRelativeDate(date, lang, options) {
    const includeTime = options?.includeTime;
    const includeDate = options?.includeDate;
    const dateObj = new Date(date);
    const now = options?.now ?? new Date();
    const minutesDiff = differenceInMinutes(now, dateObj);
    const isFuture = minutesDiff < 0;
    const absMinutesDiff = Math.abs(minutesDiff);
    // Handle very recent past or very near future
    if (absMinutesDiff < 1) {
        return translateToString(l.justNow, lang);
    }
    // Handle minutes ago or in minutes
    if (absMinutesDiff < 60) {
        if (isFuture) {
            return translateToString(l.inMinutes, lang, { count: absMinutesDiff });
        }
        return translateToString(l.minutesAgo, lang, { count: absMinutesDiff });
    }
    // Handle today
    if (isSameDay(dateObj, now)) {
        const showTime = includeTime !== false;
        const showDate = includeDate === true;
        if (showDate && showTime) {
            return formatWithTime(translateToString(l.today, lang), dateObj, lang);
        }
        if (showDate) {
            return translateToString(l.today, lang);
        }
        if (showTime) {
            return formatTime(dateObj, lang);
        }
        return translateToString(l.today, lang);
    }
    // Handle yesterday
    if (isSameDay(dateObj, subDays(now, 1))) {
        return formatDateWithOptionalTime(translateToString(l.yesterday, lang), dateObj, lang, includeTime, includeDate);
    }
    // Handle tomorrow
    if (isSameDay(dateObj, addDays(now, 1))) {
        return formatDateWithOptionalTime(translateToString(l.tomorrow, lang), dateObj, lang, includeTime, includeDate);
    }
    // Handle this week
    const weekStart = startOfWeek(now, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
    if (isWithinInterval(dateObj, { start: weekStart, end: weekEnd })) {
        const dateString = capitalizeFirstLetter(dateObj.toLocaleDateString(lang, { weekday: 'long' }));
        return formatDateWithOptionalTime(dateString, dateObj, lang, includeTime, includeDate);
    }
    // Handle this year
    if (dateObj.getFullYear() === now.getFullYear()) {
        const dateString = dateObj.toLocaleDateString(lang, {
            month: 'long',
            day: 'numeric',
        });
        return formatDateWithOptionalTime(dateString, dateObj, lang, includeTime, includeDate);
    }
    // Handle other years
    const dateString = dateObj.toLocaleDateString(lang, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
    return formatDateWithOptionalTime(dateString, dateObj, lang, includeTime, includeDate);
}
/**
 * Formats a date string with optional time based on ternary include flags.
 * For non-today dates: undefined means date-only (no time), true forces time, false hides time.
 */
function formatDateWithOptionalTime(dateString, dateObj, lang, includeTime, includeDate) {
    const showTime = includeTime === true;
    const showDate = includeDate !== false;
    if (showDate && showTime) {
        return formatWithTime(dateString, dateObj, lang);
    }
    if (showTime) {
        return formatTime(dateObj, lang);
    }
    if (showDate) {
        return dateString;
    }
    return dateString;
}
function formatWithTime(dateString, time, lang) {
    return translateToString(l.atTime, lang, {
        date: dateString,
        time: formatTime(time, lang),
    });
}
function formatTime(time, lang) {
    return time.toLocaleTimeString(lang, {
        hour: '2-digit',
        minute: '2-digit',
    });
}
//# sourceMappingURL=formatRelativeDate.js.map